// Security configuration for the frontend application
// Based on best practices from QuickNode and React security guides

// Get API URL from environment variables
// Vite uses VITE_ prefix, but we also support REACT_APP_ for compatibility
const getApiUrl = () => {
  const reactAppUrl = import.meta.env.VITE_REACT_APP_API_URL;
  const fallbackUrl = 'http://localhost:8000';
  
  const apiUrl = reactAppUrl || fallbackUrl;
  
  // Log in development to help debug
  if (import.meta.env.DEV) {
    console.log('🔧 API Configuration:', {
      'VITE_REACT_APP_API_URL': reactAppUrl || 'not set',
      'Using API URL': apiUrl
    });
  }
  
  return apiUrl;
};

export const SECURITY_CONFIG = {
  // API Configuration
  API_URL: getApiUrl(),
  
  // Token Management
  TOKEN_STORAGE_KEY: 'access_token',
  USER_STORAGE_KEY: 'user',
  
  // Security Headers
  SECURITY_HEADERS: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  
  // Rate Limiting (client-side)
  RATE_LIMIT: {
    MAX_REQUESTS_PER_MINUTE: 60,
    MAX_REQUESTS_PER_HOUR: 1000,
  },
  
  // Session Management
  SESSION: {
    TOKEN_EXPIRY_CHECK_INTERVAL: 5 * 60 * 1000, // 5 minutes
    AUTO_LOGOUT_ON_EXPIRY: true,
  },
  
  // CORS Configuration
  CORS: {
    ALLOWED_ORIGINS: import.meta.env.VITE_REACT_APP_ALLOWED_ORIGINS?.split(',') || ['http://localhost:3000'],
  },
  
  // Feature Flags
  FEATURES: {
    ENABLE_ANALYTICS: import.meta.env.VITE_REACT_APP_ENABLE_ANALYTICS === 'true',
    ENABLE_DEBUG_MODE: import.meta.env.VITE_REACT_APP_ENABLE_DEBUG_MODE === 'true',
  },
};

// Security utility functions
export const SecurityUtils = {
  // Sanitize user input
  sanitizeInput: (input: string): string => {
    return input.replace(/[<>]/g, '');
  },
  
  // Validate email format
  isValidEmail: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },
  
  // Check if token is expired
  isTokenExpired: (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  },
  
  // Generate secure random string
  generateSecureId: (): string => {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  },
  
  // Rate limiting helper
  rateLimit: {
    requests: new Map<string, number[]>(),
    
    checkLimit: (key: string, maxRequests: number, timeWindow: number): boolean => {
      const now = Date.now();
      const userRequests = SecurityUtils.rateLimit.requests.get(key) || [];
      
      // Remove old requests outside the time window
      const recentRequests = userRequests.filter(time => now - time < timeWindow);
      
      if (recentRequests.length >= maxRequests) {
        return false; // Rate limit exceeded
      }
      
      // Add current request
      recentRequests.push(now);
      SecurityUtils.rateLimit.requests.set(key, recentRequests);
      
      return true; // Request allowed
    },
  },
};

// Security middleware for API calls
export const SecurityMiddleware = {
  // Add security headers to requests
  addSecurityHeaders: (config: any) => {
    return {
      ...config,
      headers: {
        ...SECURITY_CONFIG.SECURITY_HEADERS,
        ...config.headers,
        'Referrer-Policy': 'strict-origin-when-cross-origin',
      },
    };
  },
  
  // Validate response for security issues
  validateResponse: (response: any) => {
    // Check for suspicious headers or content
    // Note: This is just a warning, it doesn't block the response
    // Axios normalizes headers to lowercase, so we check both formats
    try {
      if (response?.headers) {
        const suspiciousHeaders = ['x-powered-by', 'server'];
        const headerKeys = Object.keys(response.headers).map(k => k.toLowerCase());
        const hasSuspiciousHeaders = suspiciousHeaders.some(header => 
          headerKeys.includes(header.toLowerCase())
        );
        
        if (hasSuspiciousHeaders && SECURITY_CONFIG.FEATURES.ENABLE_DEBUG_MODE) {
          console.warn('Suspicious headers detected in response (this is just a warning, not blocking)');
        }
      }
    } catch (error) {
      // Silently ignore errors in header validation to prevent blocking responses
      if (SECURITY_CONFIG.FEATURES.ENABLE_DEBUG_MODE) {
        console.warn('Error validating response headers:', error);
      }
    }
    
    return response;
  },
  
  // Log security events
  logSecurityEvent: (event: string, details?: any) => {
    if (SECURITY_CONFIG.FEATURES.ENABLE_DEBUG_MODE) {
      console.log(`[SECURITY] ${event}`, details);
    }
  },

}; 